import React, { useState } from "react";
import { toast } from "sonner";
import { Pencil, X, Save } from "lucide-react";
import api from "@/lib/api";
import { Card, StatusBadge, chf } from "@/components/common";

export default function StammdatenCard({ client, packages, onSaved, readOnly = false }) {
  const c = client;
  const inp = "w-full bg-black border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#A3E635]";
  const STATUS = [["in_vorbereitung", "In Vorbereitung"], ["aktiv", "Aktiv"], ["pausiert", "Pausiert"], ["gekündigt", "Gekündigt"]];
  const [edit, setEdit] = useState(false);
  const [saving, setSaving] = useState(false);
  const base = () => ({
    company_name: c.company_name || "", branche: c.branche || "", contact_person: c.contact_person || "",
    phone: c.phone || "", street: c.street || "", plz: c.plz || "", ort: c.ort || "",
    domain: c.domain || "", app_domain: c.app_domain || "", package_id: c.package?.id || c.package_id || "",
    contract_months: c.contract_months ?? 24, status: c.status || "in_vorbereitung",
    setup_fee: c.setup_fee ?? "", monthly_price: c.monthly_price ?? "", primary_color: c.primary_color || "#A3E635",
    language: c.language || "de", internal_notes: c.internal_notes || "",
  });
  const [f, setF] = useState(base());
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const start = () => { setF(base()); setEdit(true); };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/clients/${c.id}`, {
        company_name: f.company_name, branche: f.branche, contact_person: f.contact_person, phone: f.phone,
        street: f.street, plz: f.plz, ort: f.ort, domain: f.domain, app_domain: f.app_domain,
        package_id: f.package_id, contract_months: Number(f.contract_months) || 24, status: f.status,
        primary_color: f.primary_color, language: f.language, internal_notes: f.internal_notes,
        setup_fee: f.setup_fee === "" ? null : Number(f.setup_fee),
        monthly_price: f.monthly_price === "" ? null : Number(f.monthly_price),
      });
      toast.success("Stammdaten gespeichert"); setEdit(false); onSaved();
    } catch { toast.error("Speichern fehlgeschlagen"); } finally { setSaving(false); }
  };

  if (!edit || readOnly) {
    return (
      <Card className="p-6 lg:col-span-3" data-testid="stammdaten-card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold">Stammdaten</h3>
          {!readOnly && <button onClick={start} data-testid="stammdaten-edit"
            className="flex items-center gap-1.5 text-xs font-semibold text-gray-300 hover:text-white bg-white/5 hover:bg-white/10 rounded-lg px-3 py-1.5">
            <Pencil className="w-3.5 h-3.5" /> Bearbeiten
          </button>}
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-y-3 gap-x-6 text-sm">
          {[["Kontaktperson", c.contact_person], ["E-Mail", c.email], ["Telefon", c.phone || "—"],
            ["Adresse", c.street || "—"], ["PLZ / Ort", [c.plz, c.ort].filter(Boolean).join(" ") || "—"],
            ["Domain", c.domain], ["App-Domain", c.app_domain || "—"], ["Branche", c.branche],
            ["Paket", c.package?.name], ["Status", <StatusBadge status={c.status} />],
            ["Laufzeit", `${c.contract_months} Monate`], ["Setup-Gebühr (inkl. MWST)", chf(c.setup_fee)],
            ["Monatspreis (inkl. MWST)", chf(c.monthly_price)], ["Vertragsstart", c.contract_start]].map(([k, v], i) => (
            <div key={i} className="flex justify-between border-b border-white/5 pb-2">
              <span className="text-gray-500">{k}</span><span className="font-medium text-right">{v}</span>
            </div>
          ))}
        </div>
        {c.internal_notes && <div className="mt-4 p-3 rounded-lg bg-white/5 text-sm text-gray-300"><span className="text-gray-500 text-xs uppercase tracking-wider">Interne Notizen (nur KEWAG)</span><br />{c.internal_notes}</div>}
      </Card>
    );
  }

  const L = ({ label, k, type, placeholder }) => (
    <div>
      <label className="text-xs text-gray-400 mb-1 block">{label}</label>
      <input className={inp} type={type || "text"} data-testid={`sd-${k}`} placeholder={placeholder} value={f[k]} onChange={(e) => set(k, e.target.value)} />
    </div>
  );

  return (
    <Card className="p-6 lg:col-span-3" data-testid="stammdaten-edit-form">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold">Stammdaten bearbeiten</h3>
        <button onClick={() => setEdit(false)} className="text-gray-400 hover:text-white" data-testid="stammdaten-cancel"><X className="w-5 h-5" /></button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <L label="Firma" k="company_name" />
        <L label="Branche" k="branche" />
        <L label="Kontaktperson" k="contact_person" />
        <div>
          <label className="text-xs text-gray-400 mb-1 block">E-Mail (Login – nicht änderbar)</label>
          <input className={`${inp} opacity-60 cursor-not-allowed`} value={c.email} disabled data-testid="sd-email-readonly" />
        </div>
        <L label="Telefon" k="phone" />
        <L label="Domain" k="domain" placeholder="firma.ch" />
        <L label="App-Domain" k="app_domain" placeholder="app.firma.ch" />
        <L label="Strasse" k="street" />
        <L label="PLZ" k="plz" />
        <L label="Ort" k="ort" />
        <div>
          <label className="text-xs text-gray-400 mb-1 block">Paket</label>
          <select className={inp} data-testid="sd-package" value={f.package_id} onChange={(e) => set("package_id", e.target.value)}>
            <option value="">— wählen —</option>
            {packages.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs text-gray-400 mb-1 block">Status</label>
          <select className={inp} data-testid="sd-status" value={f.status} onChange={(e) => set("status", e.target.value)}>
            {STATUS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <L label="Laufzeit (Monate)" k="contract_months" type="number" />
        <L label="Setup-Gebühr (CHF, inkl. MWST)" k="setup_fee" type="number" />
        <L label="Monatspreis (CHF, inkl. MWST)" k="monthly_price" type="number" />
        <div>
          <label className="text-xs text-gray-400 mb-1 block">Sprache</label>
          <select className={inp} data-testid="sd-language" value={f.language} onChange={(e) => set("language", e.target.value)}>
            <option value="de">Deutsch</option><option value="fr">Français</option><option value="it">Italiano</option><option value="en">English</option>
          </select>
        </div>
        <div>
          <label className="text-xs text-gray-400 mb-1 block">Akzentfarbe</label>
          <div className="flex items-center gap-2">
            <input type="color" data-testid="sd-color" value={f.primary_color} onChange={(e) => set("primary_color", e.target.value)} className="w-10 h-9 rounded bg-black border border-white/10" />
            <input className={inp} value={f.primary_color} onChange={(e) => set("primary_color", e.target.value)} />
          </div>
        </div>
      </div>
      <div className="mt-4">
        <label className="text-xs text-gray-400 mb-1 block">Interne Notizen (nur KEWAG — für Kunden unsichtbar)</label>
        <textarea className={`${inp} min-h-[72px]`} data-testid="sd-notes" value={f.internal_notes} onChange={(e) => set("internal_notes", e.target.value)} />
      </div>
      <div className="flex items-center gap-3 mt-5">
        <button onClick={save} disabled={saving} data-testid="sd-save"
          className="bg-[#A3E635] hover:bg-[#BEF264] text-black font-bold py-2 px-4 rounded-lg text-sm flex items-center gap-2 disabled:opacity-60">
          <Save className="w-4 h-4" /> Stammdaten speichern
        </button>
        <button onClick={() => setEdit(false)} className="text-sm text-gray-400 hover:text-white px-3 py-2">Abbrechen</button>
      </div>
    </Card>
  );
}
